import React, { useEffect, useState } from "react"

// Live destination-weather chip. It loads AFTER the page renders, from Open-Meteo
// (free, no API key), and fails silently: if the geocode or forecast is slow or
// errors, it renders nothing, never blocks the page, and never touches the
// page's meta/robots/canonical. The route page's indexable content does not
// depend on it, so a weather outage can never de-index a route (the hard rule
// learned from the CMS-500 incident). Results are cached per city for 2 hours.

const CODE_LABEL = {
  0: "clear sky", 1: "mainly clear", 2: "partly cloudy", 3: "overcast",
  45: "fog", 48: "fog", 51: "light drizzle", 53: "drizzle", 55: "drizzle",
  56: "freezing drizzle", 57: "freezing drizzle",
  61: "light rain", 63: "rain", 65: "heavy rain", 66: "freezing rain", 67: "freezing rain",
  71: "light snow", 73: "snow", 75: "heavy snow", 77: "snow grains",
  80: "light showers", 81: "showers", 82: "heavy showers",
  85: "snow showers", 86: "snow showers",
  95: "thunderstorm", 96: "thunderstorm", 99: "thunderstorm",
}

const iconKind = (code) => {
  if (code === 0 || code === 1) return "sun"
  if (code === 51 || code === 53 || code === 55 || (code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return "rain"
  if (code >= 71 && code <= 86) return "snow"
  if (code >= 95) return "storm"
  return "cloud"
}

const WeatherGlyph = ({ kind }) => {
  const common = { width: 16, height: 16, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.9, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": true }
  if (kind === "sun") return <svg {...common}><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4 12H2M22 12h-2M5 5l1.5 1.5M17.5 17.5 19 19M19 5l-1.5 1.5M6.5 17.5 5 19" /></svg>
  if (kind === "rain") return <svg {...common}><path d="M7 16a4 4 0 0 1 0-8 5 5 0 0 1 9.6-1.3A3.5 3.5 0 0 1 17 16H7Z" /><path d="M8 19l-1 2M12 19l-1 2M16 19l-1 2" /></svg>
  if (kind === "storm") return <svg {...common}><path d="M7 15a4 4 0 0 1 0-8 5 5 0 0 1 9.6-1.3A3.5 3.5 0 0 1 17 15" /><path d="M12 12l-2 4h3l-2 4" /></svg>
  if (kind === "snow") return <svg {...common}><path d="M7 15a4 4 0 0 1 0-8 5 5 0 0 1 9.6-1.3A3.5 3.5 0 0 1 17 15Z" /><path d="M9 19h.01M13 19h.01M11 21h.01" /></svg>
  return <svg {...common}><path d="M7 18a4 4 0 0 1 0-8 5 5 0 0 1 9.6-1.3A3.5 3.5 0 0 1 17 18H7Z" /></svg>
}

const WeatherChip = ({ city }) => {
  const [w, setW] = useState(null)

  useEffect(() => {
    if (!city || typeof fetch !== "function") return
    let alive = true

    const cacheKey = `pulpit_wx_${city.toLowerCase()}`
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) || "null")
      if (cached && Date.now() - cached.at < 2 * 60 * 60 * 1000) {
        setW(cached.data)
        return
      }
    } catch { /* ignore cache read errors */ }

    const run = async () => {
      try {
        const geo = await fetch(
          `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(city)}&count=1&country=IN&language=en&format=json`
        ).then((r) => (r.ok ? r.json() : null))
        const loc = geo?.results?.[0]
        if (!loc || typeof loc.latitude !== "number") return

        const fc = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${loc.latitude}&longitude=${loc.longitude}&current=temperature_2m,weather_code&timezone=auto`
        ).then((r) => (r.ok ? r.json() : null))
        const temp = fc?.current?.temperature_2m
        const code = fc?.current?.weather_code
        if (!alive || typeof temp !== "number") return

        const data = { temp: Math.round(temp), label: CODE_LABEL[code] || "", icon: iconKind(code) }
        setW(data)
        try { sessionStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), data })) } catch { /* ignore */ }
      } catch {
        // fail silently: no weather shown, no error surfaced, no layout shift
      }
    }
    run()
    return () => { alive = false }
  }, [city])

  if (!w) return null

  return (
    <span className="rp-weather" title={`Current weather in ${city}, source Open-Meteo`}>
      <span className="rp-weather__ic"><WeatherGlyph kind={w.icon} /></span>
      <span className="rp-weather__t">
        {city} {w.temp}&deg;C{w.label ? `, ${w.label}` : ""}
      </span>
      <span className="rp-weather__src">Open-Meteo</span>
    </span>
  )
}

export default WeatherChip
